import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useToast } from 'src/shared/components/ui/use-toast';
import { useSocketProvider } from 'src/shared/contexts/SocketContext';

type SocketOptions<T = unknown> = {
  roomId: string[];
  event: string;
  disabled?: boolean;
  callback?: (data: T) => void;
  debug?: boolean;
};

export function useSocket<T = unknown>({
  roomId,
  event,
  disabled,
  callback,
  debug,
}: SocketOptions<T>): {
  history: T[];
  data: T | undefined;
  socketReady: boolean;
  emit: (event: string, data?: any) => void;
} {
  const { socketReady, addListener, removeListener, emit: _emit } = useSocketProvider();
  const listenerId = useRef(crypto.randomUUID());
  const callbackRef = useRef<(data: T) => void>(actualCallback);
  const [history, setHistory] = useState<T[]>([]);
  const { toast } = useToast();

  const room = roomId.join(':');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function actualCallback(data: T) {
    setHistory((prev) => [data, ...prev.slice(0, 19)]);
    callback?.(data);
    if (debug && process.env.NODE_ENV === 'development') {
      toast({
        variant: 'info',
        title: 'Socket Event',
        description: JSON.stringify(data, null, 2),
      });
    }
  }

  useEffect(() => {
    callbackRef.current = actualCallback;
  }, [actualCallback]);

  useEffect(() => {
    if (disabled) {
      // Remove listener when disabled
      removeListener(room, event, listenerId.current);
      return;
    }
    // Add listener when enabled - this will also handle event name changes
    // by removing the old listener (if any) and adding the new one
    addListener(room, event, listenerId.current, callbackRef as MutableRefObject<(data: unknown) => void>);

    // Cleanup: remove listener when room/event changes or component unmounts
    return () => {
      removeListener(room, event, listenerId.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, event, disabled]);

  function emit(event: string, data?: any) {
    _emit(room, event, data);
  }

  return { socketReady, emit, history, data: history[0] as T | undefined };
}
